/* global axios */

import Cookies from 'js-cookie';
import endPoints from './endPoints';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';

export default {
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },
  logout() {
    const urlData = endPoints('logout');
    // Defensive logout: clear session cookies + IndexedDB on BOTH success AND
    // failure. Without this, a server call that gets dropped/stripped by a
    // corporate proxy (Zscaler etc.), 5xx, or a network outage leaves the
    // browser holding stale auth cookies → the user looks "still logged in"
    // and the next request fights with a server-side-invalidated session.
    //
    // Safe to call unconditionally: these helpers only touch Chatwoot's own
    // session storage (cw_d_session_info + access-token / client / uid /
    // expiry cookies, the DRAFT_MESSAGES Chatwoot draft key, the
    // IMPERSONATION_USER sessionStorage marker, and Chatwoot's IndexedDB
    // caches). They DO NOT touch Fangate tokens, layout widths, onboarding
    // state, our Fangate drafts, mute/block lists, or any other per-feature
    // preference we persist — those all survive logout.
    return new Promise((resolve, reject) => {
      axios
        .delete(urlData.url)
        .then(resolve)
        .catch(reject)
        .finally(() => {
          try { deleteIndexedDBOnLogout(); } catch (_) { /* best-effort */ }
          // clearCookiesOnLogout() also triggers the redirect to '/', so this
          // is the last step in either case.
          try { clearCookiesOnLogout(); }       catch (_) { /* best-effort */ }
        });
    });
  },
  hasAuthCookie() {
    return !!Cookies.get('cw_d_session_info');
  },
  getAuthData() {
    if (this.hasAuthCookie()) {
      const savedAuthInfo = Cookies.get('cw_d_session_info');
      return JSON.parse(savedAuthInfo || '{}');
    }
    return false;
  },
  profileUpdate({ displayName, avatar, ...profileAttributes }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const hasValue = profileAttributes[key] === undefined;
      if (!hasValue) {
        formData.append(`profile[${key}]`, profileAttributes[key]);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (avatar) {
      formData.append('profile[avatar]', avatar);
    }
    return axios.put(endPoints('profileUpdate').url, formData);
  },

  profilePasswordUpdate({ currentPassword, password, passwordConfirmation }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      },
    });
  },

  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },

  updateAutoOffline(accountId, autoOffline = false) {
    return axios.post(endPoints('autoOffline').url, {
      profile: { account_id: accountId, auto_offline: autoOffline },
    });
  },

  deleteAvatar() {
    return axios.delete(endPoints('deleteAvatar').url);
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  setActiveAccount({ accountId }) {
    const urlData = endPoints('setActiveAccount');
    return axios.put(urlData.url, {
      profile: {
        account_id: accountId,
      },
    });
  },
  resendConfirmation() {
    const urlData = endPoints('resendConfirmation');
    return axios.post(urlData.url);
  },
  resetAccessToken() {
    const urlData = endPoints('resetAccessToken');
    return axios.post(urlData.url);
  },
};
